import dayjs from 'dayjs';
import { AgentSessionLabel } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { AgentSource } from '../../../../api/events-client/events-client.type';
import {
  AGENT_SESSION_LABEL_VALUES,
  AGENT_SOURCE_VALUES,
  DATE_PRESET_MS,
  type DateRangePreset,
  DEFAULT_DATE_RANGE,
  type SessionsFiltersValue,
} from './session-filters.type';

/**
 * Splits a comma-separated string into an array of trimmed, non-empty strings.
 * Tokens are trimmed so a hand-edited `a, b` (with a space after the comma)
 * round-trips to the same values as `a,b`.
 * @param {string | null} raw - The raw URL parameter value, or `null` if absent.
 * @returns {string[]} An array of trimmed, non-empty string tokens.
 */
const parseCSV = (raw: string | null): string[] =>
  raw
    ? raw
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean)
    : [];

/**
 * Coerces a URL parameter string to a tri-state boolean.
 * @param {string | null} raw - The raw URL parameter value, or `null` if absent.
 * @returns {boolean | null} `true` for "true"/"1", `false` for "false"/"0", or `null` for anything else.
 */
const parseHasTask = (raw: string | null): boolean | null => {
  if (raw === 'true' || raw === '1') return true;
  if (raw === 'false' || raw === '0') return false;
  return null;
};

/**
 * Parses a URL parameter into a valid {@link DateRangePreset}, falling back to the default if unrecognised.
 * @param {string | null} raw - The raw URL parameter value, or `null` if absent.
 * @returns {DateRangePreset} A known `DateRangePreset` value, or {@link DEFAULT_DATE_RANGE} when the input is invalid.
 */
const parseDateRange = (raw: string | null): DateRangePreset => {
  if (raw === 'all' || raw === '24h' || raw === '7d' || raw === '30d' || raw === '90d' || raw === 'custom') return raw;
  return DEFAULT_DATE_RANGE;
};

/**
 * Validates and parses a URL parameter as an ISO 8601 date string (YYYY-MM-DD).
 *
 * Accept only well-formed YYYY-MM-DD strings that parse to a real calendar date.
 * Anything else (empty, malformed, or out-of-range) is treated as absent so a
 * hand-edited URL can't forward garbage into the API.
 * @param {string | null} raw - The raw URL parameter value, or `null` if absent.
 * @returns {string | null} The original `raw` string if valid, otherwise `null`.
 */
const parseIsoDate = (raw: string | null): string | null => {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = Date.parse(`${raw}T00:00:00Z`);
  return Number.isNaN(parsed) ? null : raw;
};

/**
 * Deserialises a {@link URLSearchParams} instance into a {@link SessionsFiltersValue}.
 *
 * Each parameter is validated before use so stale or hand-edited URLs cannot
 * inject invalid values into the API.
 * @param {URLSearchParams} sp - The `URLSearchParams` read from the current location.
 * @returns {SessionsFiltersValue} A fully-populated `SessionsFiltersValue` with safe defaults for missing/invalid params.
 */
const readFiltersFromSearch = (sp: URLSearchParams): SessionsFiltersValue => ({
  q: sp.get('q') ?? '',
  // Validate against the enum so stale/hand-edited URLs can't inject invalid values.
  sources: parseCSV(sp.get('source')).filter((value): value is AgentSource => AGENT_SOURCE_VALUES.has(value)),
  userIds: parseCSV(sp.get('user_id')),
  labels: parseCSV(sp.get('label')).filter((value): value is AgentSessionLabel =>
    AGENT_SESSION_LABEL_VALUES.has(value),
  ),
  dateRange: parseDateRange(sp.get('started')),
  customStart: parseIsoDate(sp.get('start_date')),
  customEnd: parseIsoDate(sp.get('end_date')),
  hasTask: parseHasTask(sp.get('has_task')),
  hideEmpty: sp.get('hide_empty') !== 'false',
  // Not enum-validated: families are derived server-side from raw model ids
  // and unmatched ids pass through verbatim, so the set is open-ended.
  modelFamilies: parseCSV(sp.get('model_family')),
});

/**
 * Returns the ISO 8601 date string (YYYY-MM-DD) for the moment `ms` milliseconds in the past.
 * @param {number} ms - The number of milliseconds to subtract from the current time.
 * @returns {string} An ISO date string, e.g. `"2026-04-15"`.
 */
const isoDateDaysAgo = (ms: number): string => new Date(Date.now() - ms).toISOString().slice(0, 10);

/**
 * Resolves the `start` and `end` date strings from a {@link SessionsFiltersValue}.
 *
 * - `"all"` — returns an empty object (no date bounds).
 * - `"custom"` — returns the user-supplied `customStart`/`customEnd` values.
 * - Any preset (`"24h"`, `"7d"`, etc.) — computes `start` relative to now using {@link DATE_PRESET_MS}.
 * @param {SessionsFiltersValue} value - The current filter state.
 * @returns {{ start?: string; end?: string }} An object with optional `start` and `end` ISO date strings.
 */
const resolveStartEnd = (value: SessionsFiltersValue): { start?: string; end?: string } => {
  if (value.dateRange === 'all') return {};
  if (value.dateRange === 'custom') {
    return {
      start: value.customStart ?? undefined,
      end: value.customEnd ?? undefined,
    };
  }
  // "Last N days" is inclusive of today, and the backend treats end_date as
  // inclusive — so subtracting the full N-day duration would span N+1 calendar
  // days. Correct EVERY preset by one day to an exact N-day span (e.g. "Last 7
  // days" → 6 days back through today); previously only the 30-day preset was
  // corrected, leaving 24h/7d/90d overshooting by a day.
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const presetMs = DATE_PRESET_MS[value.dateRange] - ONE_DAY_MS;
  return { start: isoDateDaysAgo(presetMs) };
};

/**
 * Sets or removes a single key on a {@link URLSearchParams} instance.
 *
 * Deletes the key when `raw` is `null`, `undefined`, or an empty string;
 * otherwise calls `params.set(key, raw)`.
 * @param {URLSearchParams} params - The `URLSearchParams` object to mutate.
 * @param {string} key - The query-string key to set or delete.
 * @param {string | null | undefined} raw - The stringified value to write, or a nullish/empty value to trigger deletion.
 */
const setOrDelete = (params: URLSearchParams, key: string, raw: string | null | undefined): void => {
  if (raw == null || raw === '') params.delete(key);
  else params.set(key, raw);
};

/**
 * Converts a raw date string (YYYY-MM-DD) to a `Date` object, or `null` if the input is invalid.
 * @param {string | null} raw - The raw date string, or `null` if absent.
 * @returns {Date | null} A `Date` object if the input is valid, otherwise `null`.
 */
const toDateOrNull = (raw: string | null): Date | null => {
  if (!raw) return null;
  // dayjs parses date-only strings at LOCAL midnight. `new Date('YYYY-MM-DD')`
  // parses at UTC midnight, which renders as the previous day in timezones
  // behind UTC — the date picker would show (and re-emit) date − 1.
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Converts a `Date` object or ISO date string (YYYY-MM-DD) to a valid ISO date string, or `null` if the input is invalid.
 * @param {Date | string | null | undefined} d - The date to convert, or `null` if absent.
 * @returns {string | null} A valid ISO date string if the input is valid, otherwise `null`.
 */
const toIsoDateOrNull = (d: Date | string | null | undefined): string | null => {
  if (!d) return null;
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};
export { parseIsoDate, readFiltersFromSearch, resolveStartEnd, setOrDelete, toDateOrNull, toIsoDateOrNull };
